import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiSearch, FiEye, FiPlus, FiFile, FiDownload, FiUpload, FiAlertCircle, FiEdit2, FiTrash2, FiX, FiAlertTriangle } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
    fetchCompanyId,
    fetchCCTVHardware,
    createCCTVHardware,
    updateCCTVHardware,
    deleteCCTVHardware,
    fetchBranches,
    fetchDeployments,
    type CCTVHardware,
    type CCTVHardwareFormData
} from '../service/cctvHardwareService';
import Table, { Column } from '../../../../components/Table';

export default function ManageCCTVHardware() {
    const [hardware, setHardware] = useState<CCTVHardware[]>([]);
    const [filteredHardware, setFilteredHardware] = useState<CCTVHardware[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [regionFilter, setRegionFilter] = useState('All');
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedHardware, setSelectedHardware] = useState<CCTVHardware | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedHardware, setEditedHardware] = useState<Partial<CCTVHardware> | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [hardwareToDelete, setHardwareToDelete] = useState<CCTVHardware | null>(null);
    const [loading, setLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [branches, setBranches] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [vendorMap, setVendorMap] = useState<Map<string, string>>(new Map());
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Excel upload states
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadResults, setUploadResults] = useState<any[]>([]);
    const [undoStack, setUndoStack] = useState<any[]>([]); // Store previous hardware data for undo

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const pso = useAppSelector((s) => s.user.pso);
    const designation = (pso?.designation || '').replace(/_/g, ' ').toLowerCase();
    const isCctvIncharge = designation.includes('cctv');
    const isMIS = designation.includes('mis');
    // Build candidate IDs that a branch.psoId might store for this PSO (memoized to prevent infinite loops)
    const psoIdCandidates: string[] = useMemo(() => [
        ...(pso?.authUid ? [pso.authUid] : []),
        ...(((pso as any)?.psoId) ? [(pso as any).psoId as string] : []),
        ...(uid ? [uid] : []),
    ], [pso?.authUid, (pso as any)?.psoId, uid]);
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const isIncharge = designation.includes('incharge');
    const isPSO = userRole === 'pso' && !isIncharge;

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const loadCompanyId = async () => {
            try {
                const id = await fetchCompanyId(uid, isBankUser);
                if (id) {
                    setCompanyId(id);
                }
            } catch (err) {
                console.error('Error fetching company ID:', err);
            }
        };

        loadCompanyId();
    }, [uid, isBankUser]);

    // Fetch branches
    useEffect(() => {
        if (!companyId) return;

        const loadBranches = async () => {
            try {
                const branchesList = await fetchBranches(companyId);
                setBranches(branchesList);
            } catch (err) {
                console.error('Error fetching branches:', err);
            }
        };

        loadBranches();
    }, [companyId]);

    // Fetch hardware (only load data, filtering happens in separate useEffect)
    useEffect(() => {
        if (!companyId || branches.length === 0 || (!isBankUser && !isCctvIncharge && !isMIS && psoIdCandidates.length === 0)) return;

        const loadHardware = async () => {
            setLoading(true);
            try {
                const hardwareList = await fetchCCTVHardware(companyId);
                // Fetch ALL deployments with vendor names (not filtered by PSO) so we can show vendors for all branches
                // Pass isBankUser=true to fetchDeployments to get all deployments regardless of PSO
                const fetchedVendorMap = await fetchDeployments(companyId, uid || '', true);

                setVendorMap(fetchedVendorMap);
                setHardware(hardwareList);
            } catch (err) {
                console.error(err);
                setToastMessage('Failed to load CCTV hardware');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadHardware();
    }, [companyId, branches.length, isBankUser, isCctvIncharge, isMIS, psoIdCandidates.length, uid]);

    // Filter and merge hardware (runs when hardware, branches, or filters change)
    useEffect(() => {
        // For bank users, CCTV incharge, and MIS, allow filtering even if vendorMap is empty
        const canProceedWithoutVendorMap = isBankUser || isCctvIncharge || isMIS;
        if (!companyId || branches.length === 0 || (!canProceedWithoutVendorMap && vendorMap.size === 0) || (!isBankUser && !isCctvIncharge && !isMIS && psoIdCandidates.length === 0)) {
            // If hardware is still loading, don't clear the list
            if (loading) return;
            // Otherwise set empty list
            setFilteredHardware([]);
            return;
        }

        // Filter branches to only show those assigned to this PSO (unless bank user, CCTV incharge, or MIS)
        // For regular PSO users, show branches where psoId matches OR where psoId is not set (unassigned)
        const assignedBranches = branches.filter((branch: any) => {
            if (isBankUser || isCctvIncharge || isMIS) return true;
            // If branch has a psoId, it must match one of the current user's PSO IDs
            if (branch.psoId) {
                return psoIdCandidates.includes(branch.psoId);
            }
            // If branch has no psoId, show it (unassigned branches)
            return true;
        });

        // Merge branches with hardware data
        const hardwareMap = new Map();
        hardware.forEach(hw => {
            if (hw.branchId) {
                hardwareMap.set(hw.branchId, hw);
            }
        });

        let list = assignedBranches.map(branch => {
            const existingHardware = hardwareMap.get(branch.id);
            // Get vendor name from deployment vendors (vendorMap contains all deployments)
            const vendorNameFromDeployment = (vendorMap && vendorMap.get) ? vendorMap.get(branch.id) || '' : '';
            const regionName = branch.locationName || '';

            if (existingHardware) {
                // Prefer vendor from deployment vendors, fallback to hardware vendor field
                const finalVendorName = vendorNameFromDeployment || (existingHardware as any).vendor || (existingHardware as any).vendorName || '';
                return {
                    ...existingHardware,
                    vendorName: finalVendorName,
                    regionName: regionName || (existingHardware as any).regionName || '',
                };
            }
            return {
                id: `new-${branch.id}`,
                branchId: branch.id,
                branchCode: branch.branchCode || '',
                branchName: branch.address || '',
                cluster: '',
                region: '',
                vendor: '',
                vendorName: vendorNameFromDeployment,
                regionName: regionName,
                dvrNvr: '',
                noOfDvrNvr: 0,
                noOfCamera: 0,
                hdAnalog: '',
                noOf5MpCamera: 0,
                noOfHdd: 0,
                installationStatus: 'pending' as const,
                installationDate: '',
                location: '',
                remarks: '',
                companyId: companyId,
            } as CCTVHardware & { vendorName?: string; regionName?: string };
        });

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(item => {
                const branchCode = (item.branchCode || '').toString().toLowerCase();
                const branchName = (item.branchName || '').toString().toLowerCase();
                const vendorName = ((item as any).vendorName || '').toString().toLowerCase();
                const regionName = ((item as any).regionName || '').toString().toLowerCase();
                const cluster = (item.cluster || '').toString().toLowerCase();
                const region = (item.region || '').toString().toLowerCase();
                const vendor = (item.vendor || '').toString().toLowerCase();
                const location = (item.location || '').toString().toLowerCase();
                const remarks = (item.remarks || '').toString().toLowerCase();
                const installationStatus = (item.installationStatus || '').toString().toLowerCase();
                return branchCode.includes(term) ||
                    branchName.includes(term) ||
                    vendorName.includes(term) ||
                    regionName.includes(term) ||
                    cluster.includes(term) ||
                    region.includes(term) ||
                    vendor.includes(term) ||
                    location.includes(term) ||
                    remarks.includes(term) ||
                    installationStatus.includes(term);
            });
        }

        // Apply region filter
        if (regionFilter !== 'All') {
            list = list.filter(item => {
                const itemRegion = ((item as any).regionName || item.region || '').toString().toLowerCase();
                return itemRegion === regionFilter.toLowerCase();
            });
        }

        setFilteredHardware(list);
        setCurrentPage(1);
    }, [hardware, branches, searchTerm, regionFilter, companyId, isBankUser, isCctvIncharge, isMIS, psoIdCandidates, vendorMap, loading]);

    const totalPages = Math.ceil(filteredHardware.length / perPage);
    const paginatedData = filteredHardware.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Format date
    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        if (typeof date === 'string') {
            return new Date(date).toLocaleDateString();
        }
        return new Date(date).toLocaleDateString();
    };

    // Get status badge
    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'installed') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'sent') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status}
            </span>
        );
    };

    // Export to Excel
    const exportToExcel = () => {
        if (filteredHardware.length === 0) {
            setToastMessage('No hardware to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const worksheetData = filteredHardware.map(item => ({
            'Branch Code': item.branchCode || 'N/A',
            'Branch Name': item.branchName || 'N/A',
            'Cluster': item.cluster || 'N/A',
            'Region': (item as any).regionName || item.region || 'N/A',
            'Vendor Name': (item as any).vendorName || item.vendor || 'N/A',
            'DVR/NVR': item.dvrNvr || 'N/A',
            'NO. OF DVR/NVR': item.noOfDvrNvr || 0,
            'NO. OF CAMERA': item.noOfCamera || 0,
            'HD/ANALOG': item.hdAnalog || 'N/A',
            'NO. OF 5 MP CAMERA': item.noOf5MpCamera || 0,
            'NO. OF HDD': item.noOfHdd || 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'CCTV Hardware');

        const fileName = `CCTV_Hardware_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Download sample Excel format
    const downloadSampleFormat = () => {
        const sampleData = [
            {
                'Branch Code': '1895',
                'DVR/NVR': 'DVR',
                'NO. OF DVR/NVR': 2,
                'NO. OF CAMERA': 16,
                'HD/ANALOG': 'HD',
                'NO. OF 5 MP CAMERA': 8,
                'NO. OF HDD': 4
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'CCTV Hardware Sample');

        const fileName = `CCTV_Hardware_Sample_Format.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Sample Excel format downloaded successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Import from Excel
    const handleImportExcel = () => {
        fileInputRef.current?.click();
    };

    const processExcelFile = async (file: File) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setUploadResults([]);
        setUndoStack([]);

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

            const results: any[] = [];
            const undoData: any[] = [];

            // Fetch existing hardware to check for updates
            const existingHardware = await fetchCCTVHardware(companyId);
            const hardwareByBranchId = new Map<string, CCTVHardware>();
            existingHardware.forEach(hw => {
                if (hw.branchId) {
                    hardwareByBranchId.set(hw.branchId, hw);
                }
            });

            const totalRows = jsonData.length;

            for (let index = 0; index < jsonData.length; index++) {
                const row = jsonData[index];
                const result: any = {
                    rowNumber: index + 2, // +2 because Excel rows start at 1 and we skip header
                    branchCode: row['Branch Code'] || '',
                    status: 'pending',
                    message: ''
                };

                try {
                    // Update progress
                    setUploadProgress(Math.round(((index + 1) / totalRows) * 100));

                    // Find branch by code
                    const branch = branches.find(b =>
                        String(b.branchCode || '').toLowerCase() === String(row['Branch Code'] || '').toLowerCase()
                    );

                    if (!branch) {
                        result.status = 'error';
                        result.message = `Branch with code "${row['Branch Code']}" not found`;
                        results.push(result);
                        continue;
                    }

                    // Get vendor name from deployment vendors (vendorMap contains all deployments)
                    const vendorName = (vendorMap && vendorMap.get) ? vendorMap.get(branch.id) || '' : '';

                    // Auto-populate branch details from branch data
                    const hardwareData: Partial<CCTVHardware> = {
                        branchId: branch.id,
                        branchCode: branch.branchCode,
                        branchName: branch.name || branch.address,
                        cluster: branch.cluster || '',
                        region: (branch as any).locationName || branch.region || '',
                        vendor: vendorName,
                        dvrNvr: row['DVR/NVR'] || '',
                        noOfDvrNvr: parseInt(row['NO. OF DVR/NVR'] || row['NO OF DVR/NVR'] || '0') || 0,
                        noOfCamera: parseInt(row['NO. OF CAMERA'] || row['NO OF CAMERA'] || '0') || 0,
                        hdAnalog: row['HD/ANALOG'] || '',
                        noOf5MpCamera: parseInt(row['NO. OF 5 MP CAMERA'] || row['NO OF 5 MP CAMERA'] || '0') || 0,
                        noOfHdd: parseInt(row['NO. OF HDD'] || row['NO OF HDD'] || '0') || 0,
                        installationStatus: 'pending',
                    };

                    // Check if hardware already exists for this branch
                    const existingHw = hardwareByBranchId.get(branch.id);
                    if (existingHw) {
                        // Store undo data before updating
                        undoData.push({
                            hardwareId: existingHw.id,
                            previousData: {
                                dvrNvr: existingHw.dvrNvr,
                                noOfDvrNvr: existingHw.noOfDvrNvr,
                                noOfCamera: existingHw.noOfCamera,
                                hdAnalog: existingHw.hdAnalog,
                                noOf5MpCamera: existingHw.noOf5MpCamera,
                                noOfHdd: existingHw.noOfHdd,
                            }
                        });

                        // Update existing hardware
                        await updateCCTVHardware(companyId, existingHw.id, hardwareData);
                        result.status = 'updated';
                        result.message = `Hardware updated for branch ${branch.branchCode}`;
                        result.hardwareId = existingHw.id;
                    } else {
                        // Create new hardware
                        const hardwareId = await createCCTVHardware(companyId, hardwareData);
                        result.status = 'success';
                        result.message = `Hardware created for branch ${branch.branchCode}`;
                        result.hardwareId = hardwareId;
                    }

                    results.push(result);
                } catch (err: any) {
                    console.error('Error importing row:', err);
                    result.status = 'error';
                    result.message = err.message || 'Failed to process row';
                    results.push(result);
                }
            }

            setUploadResults(results);
            setUndoStack(undoData);
            setShowUploadModal(true);

            const successCount = results.filter(r => r.status === 'success').length;
            const updatedCount = results.filter(r => r.status === 'updated').length;
            const errorCount = results.filter(r => r.status === 'error').length;

            if (successCount > 0 || updatedCount > 0) {
                setToastMessage(`✅ Import completed: ${successCount} new, ${updatedCount} updated, ${errorCount} failed`);
                setToastType('success');
            } else {
                setToastMessage(`❌ Import failed: ${errorCount} errors`);
                setToastType('error');
            }
            setShowToast(true);
        } catch (err: any) {
            console.error('Error processing Excel file:', err);
            setToastMessage('Failed to import Excel file');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUploading(false);
            setUploadProgress(0);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processExcelFile(file);
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleModalClose = () => {
        // Auto-keep if there are updates and modal is closed
        if (undoStack.length > 0 && uploadResults.some(r => r.status === 'updated')) {
            // Already kept (no undo action taken)
            setUndoStack([]);
        }
        setShowUploadModal(false);
        // Refresh hardware list
        if (companyId) {
            fetchCCTVHardware(companyId).then(hardwareList => {
                setHardware(hardwareList);
                setFilteredHardware(hardwareList);
            });
        }
    };

    const handleUndo = async () => {
        if (!companyId || undoStack.length === 0) return;

        setUploading(true);
        try {
            // Undo all updates
            for (const undoItem of undoStack) {
                if (undoItem.hardwareId && undoItem.previousData) {
                    await updateCCTVHardware(companyId, undoItem.hardwareId, undoItem.previousData);
                }
            }

            // Update results to show undone
            setUploadResults(prev => prev.map(r => {
                if (r.status === 'updated') {
                    return { ...r, status: 'undone', message: `Undone: ${r.message}` };
                }
                return r;
            }));

            setUndoStack([]);
            setToastMessage('✅ Changes undone successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh hardware list
            const hardwareList = await fetchCCTVHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);
        } catch (error: any) {
            console.error('Undo error:', error);
            setToastMessage('❌ Failed to undo changes');
            setToastType('error');
            setShowToast(true);
        } finally {
            setUploading(false);
        }
    };

    const handleKeep = () => {
        setUndoStack([]);
        setShowUploadModal(false);
        setToastMessage('✅ Changes kept');
        setToastType('success');
        setShowToast(true);

        // Refresh hardware list
        if (companyId) {
            fetchCCTVHardware(companyId).then(hardwareList => {
                setHardware(hardwareList);
                setFilteredHardware(hardwareList);
            });
        }
    };

    const downloadErrorReport = () => {
        const errorRows = uploadResults.filter(r => r.status === 'error');
        if (errorRows.length === 0) return;

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(errorRows.map(row => ({
            'Row Number': row.rowNumber,
            'Branch Code': row.branchCode,
            'Error Message': row.message
        })));
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors');
        XLSX.writeFile(workbook, 'cctv_hardware_errors.xlsx');
    };

    const downloadFinalReport = () => {
        const workbook = XLSX.utils.book_new();
        const resultsData = uploadResults.map(row => ({
            'Row Number': row.rowNumber,
            'Branch Code': row.branchCode,
            'Status': row.status,
            'Message': row.message
        }));
        const resultsWorksheet = XLSX.utils.json_to_sheet(resultsData);
        XLSX.utils.book_append_sheet(workbook, resultsWorksheet, 'Results');

        const summaryData = [
            { 'Metric': 'Total Rows', 'Count': uploadResults.length },
            { 'Metric': 'New Hardware', 'Count': uploadResults.filter(r => r.status === 'success').length },
            { 'Metric': 'Updated', 'Count': uploadResults.filter(r => r.status === 'updated').length },
            { 'Metric': 'Failed', 'Count': uploadResults.filter(r => r.status === 'error').length },
        ];
        const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

        XLSX.writeFile(workbook, 'cctv_hardware_final_report.xlsx');
    };

    // Form data
    const [formData, setFormData] = useState<Partial<CCTVHardwareFormData>>({
        branchId: '',
        cluster: '',
        region: '',
        vendor: '',
        dvrNvr: '',
        noOfDvrNvr: 0,
        noOfCamera: 0,
        hdAnalog: '',
        noOf5MpCamera: 0,
        noOfHdd: 0,
        installationDate: new Date().toISOString().split('T')[0],
        installationStatus: 'pending',
        location: '',
        remarks: '',
    });

    // Reset form
    const resetForm = () => {
        setFormData({
            branchId: '',
            cluster: '',
            region: '',
            vendor: '',
            dvrNvr: '',
            noOfDvrNvr: 0,
            noOfCamera: 0,
            hdAnalog: '',
            noOf5MpCamera: 0,
            noOfHdd: 0,
            installationDate: new Date().toISOString().split('T')[0],
            installationStatus: 'pending',
            location: '',
            remarks: '',
        });
    };

    // Handle add hardware
    const handleAddHardware = async () => {
        if (!companyId || !uid) {
            setToastMessage('Company ID or User ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.branchId) {
            setToastMessage('Please select a branch');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsSubmitting(true);
        try {
            await createCCTVHardware(companyId, formData as Partial<CCTVHardware>);

            setToastMessage('CCTV hardware added successfully');
            setToastType('success');
            setShowToast(true);
            setShowAddModal(false);
            resetForm();

            // Refresh hardware list
            const hardwareList = await fetchCCTVHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to add CCTV hardware');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle view details
    const handleViewDetails = (item: CCTVHardware) => {
        setSelectedHardware(item);
        setEditedHardware({ ...item });
        setIsEditing(false);
        setShowDetailsModal(true);
    };

    // Handle edit
    const handleEdit = () => {
        if (selectedHardware) {
            setIsEditing(true);
            setEditedHardware({ ...selectedHardware });
        }
    };

    // Handle delete
    const handleDeleteClick = (item: CCTVHardware) => {
        setHardwareToDelete(item);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!companyId || !hardwareToDelete) return;

        setDeleting(true);
        try {
            await deleteCCTVHardware(companyId, hardwareToDelete.id);

            setToastMessage('Hardware deleted successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh hardware list
            const hardwareList = await fetchCCTVHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);

            if (selectedHardware?.id === hardwareToDelete.id) {
                setShowDetailsModal(false);
                setSelectedHardware(null);
            }

            setShowDeleteModal(false);
            setHardwareToDelete(null);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to delete hardware');
            setToastType('error');
            setShowToast(true);
        } finally {
            setDeleting(false);
        }
    };

    // Calculate summary statistics

    const columns: Column<CCTVHardware>[] = [
        {
            header: 'Branch Code',
            accessor: 'branchCode',
            render: (item) => (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {item.branchCode || 'N/A'}
                </span>
            )
        },
        {
            header: 'Branch Name',
            accessor: 'branchName',
            className: 'whitespace-normal break-words max-w-[150px]',
            render: (item) => item.branchName || 'N/A'
        },
        { header: 'Cluster', accessor: 'cluster', render: (item) => item.cluster || 'N/A' },
        {
            header: 'Region',
            accessor: 'region',
            render: (item) => (item as any).regionName || item.region || 'N/A'
        },
        {
            header: 'Vendor',
            accessor: 'vendor',
            className: 'whitespace-normal break-words max-w-[120px]',
            render: (item) => (item as any).vendorName || 'N/A'
        },
        { header: 'DVR/NVR', accessor: 'dvrNvr', align: 'center', render: (item) => item.dvrNvr || 'N/A' },
        { header: '# DVR/NVR', accessor: 'noOfDvrNvr', align: 'center', render: (item) => item.noOfDvrNvr || 0 },
        { header: '# Camera', accessor: 'noOfCamera', align: 'center', render: (item) => item.noOfCamera || 0 },
        { header: 'HD/Analog', accessor: 'hdAnalog', align: 'center', render: (item) => item.hdAnalog || 'N/A' },
        { header: '# 5MP', accessor: 'noOf5MpCamera', align: 'center', render: (item) => item.noOf5MpCamera || 0 },
        { header: '# HDD', accessor: 'noOfHdd', align: 'center', render: (item) => item.noOfHdd || 0 },
        {
            header: 'Actions',
            accessor: 'id',
            align: 'center',
            hidden: !isPSO,
            render: (item) => (
                <div className="flex gap-2 justify-center">
                    <button
                        onClick={() => handleViewDetails(item)}
                        className="inline-flex items-center justify-center rounded border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {isPSO && (
                        <button
                            onClick={() => {
                                setSelectedHardware(item);
                                setEditedHardware({ ...item });
                                setIsEditing(true);
                                setShowDetailsModal(true);
                            }}
                            className="inline-flex items-center justify-center rounded border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-700 transition-all hover:border-green-500 hover:bg-green-50 hover:text-green-600 focus:outline-none focus:ring-1 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-400"
                            title="Edit"
                        >
                            <Icon icon={FiEdit2} className="h-3 w-3" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    const headerActions = (
        <div className="flex gap-3">
            {isBankUser && (
                <>
                    <Button variant="secondary" onClick={handleImportExcel} className="flex items-center gap-2">
                        <Icon icon={FiUpload} className="h-4 w-4" />
                        Import Excel
                    </Button>
                    <Button variant="secondary" onClick={downloadSampleFormat} className="flex items-center gap-2">
                        <Icon icon={FiDownload} className="h-4 w-4" />
                        Download Sample
                    </Button>
                </>
            )}
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export Excel
            </Button>
        </div>
    );

    const filters = (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
                <Icon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search by branch code, address, region, vendor name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                />
            </div>
            <div className="relative">
                <select
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                >
                    <option value="All">All Regions</option>
                    {Array.from(new Set(
                        branches
                            .filter((branch: any) => {
                                if (isBankUser || isCctvIncharge || isMIS) return true;
                                if (branch.psoId) {
                                    return psoIdCandidates.includes(branch.psoId);
                                }
                                return true;
                            })
                            .map((branch: any) => branch.locationName || '')
                            .filter((region: string) => region.trim() !== '')
                    ))
                        .sort((a, b) => a.localeCompare(b))
                        .map((region) => (
                            <option key={region} value={region}>{region}</option>
                        ))}
                </select>
            </div>
            {(searchTerm || regionFilter !== 'All') && (
                <Button
                    variant="secondary"
                    onClick={() => {
                        setSearchTerm('');
                        setRegionFilter('All');
                        setCurrentPage(1);
                    }}
                    className="text-sm whitespace-nowrap"
                >
                    Clear Search
                </Button>
            )}
        </div>
    );

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
            />
            <div className="space-y-6">
                {/* Toast */}
                {showToast && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toastMessage}
                                type={toastType}
                                onClose={() => setShowToast(false)}
                            />
                        </div>
                    </div>
                )}

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No hardware found matching your search' : 'No hardware available'}
                    headerActions={headerActions}
                    filters={filters}
                    title="Manage CCTV Hardware"
                    description="Track CCTV hardware installations and shipments"
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                />
            </div>









            {/* Add Hardware Modal */}
            {
                showAddModal && (
                    <Modal
                        isOpen={showAddModal}
                        onClose={() => {
                            setShowAddModal(false);
                            resetForm();
                        }}
                        title="Add CCTV Hardware"
                        size="lg"
                    >
                        <div className="p-6">
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                handleAddHardware();
                            }}>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Branch <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={formData.branchId || ''}
                                                onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                required
                                            >
                                                <option value="">Select Branch</option>
                                                {branches
                                                    .filter((branch: any) => {
                                                        if (isBankUser || isCctvIncharge || isMIS) return true;
                                                        // If branch has a psoId, it must match one of the current user's PSO IDs
                                                        if (branch.psoId) {
                                                            return psoIdCandidates.includes(branch.psoId);
                                                        }
                                                        // If branch has no psoId, show it (unassigned branches)
                                                        return true;
                                                    })
                                                    .map((branch) => (
                                                        <option key={branch.id} value={branch.id}>{branch.branchCode} - {branch.address}</option>
                                                    ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Installation Status <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={formData.installationStatus || 'pending'}
                                                onChange={(e) => setFormData({ ...formData, installationStatus: e.target.value as any })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                required
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="sent">Sent</option>
                                                <option value="installed">Installed</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Cluster
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.cluster || ''}
                                                onChange={(e) => setFormData({ ...formData, cluster: e.target.value })}
                                                placeholder="Enter cluster"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Region
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.region || ''}
                                                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                                                placeholder="Enter region"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            CCTV VENDOR
                                        </label>
                                        <TextField
                                            type="text"
                                            value={formData.vendor || ''}
                                            onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                                            placeholder="Enter CCTV vendor name"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                DVR/NVR
                                            </label>
                                            <select
                                                value={formData.dvrNvr || ''}
                                                onChange={(e) => setFormData({ ...formData, dvrNvr: e.target.value })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="">Select Type</option>
                                                <option value="DVR">DVR</option>
                                                <option value="NVR">NVR</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF DVR/NVR
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOfDvrNvr || 0}
                                                onChange={(e) => setFormData({ ...formData, noOfDvrNvr: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF CAMERA
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOfCamera || 0}
                                                onChange={(e) => setFormData({ ...formData, noOfCamera: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                HD/ANALOG
                                            </label>
                                            <select
                                                value={formData.hdAnalog || ''}
                                                onChange={(e) => setFormData({ ...formData, hdAnalog: e.target.value })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="">Select Type</option>
                                                <option value="HD">HD</option>
                                                <option value="ANALOG">ANALOG</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF 5 MP CAMERA
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOf5MpCamera || 0}
                                                onChange={(e) => setFormData({ ...formData, noOf5MpCamera: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                NO. OF HDD
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.noOfHdd || 0}
                                                onChange={(e) => setFormData({ ...formData, noOfHdd: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Installation Date
                                            </label>
                                            <TextField
                                                type="date"
                                                value={formData.installationDate || ''}
                                                onChange={(e) => setFormData({ ...formData, installationDate: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Location
                                            </label>
                                            <TextField
                                                type="text"
                                                value={formData.location || ''}
                                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                                placeholder="Enter location"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Remarks
                                        </label>
                                        <textarea
                                            value={formData.remarks || ''}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            rows={3}
                                            placeholder="Enter remarks"
                                        />
                                    </div>
                                </div>
                                <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowAddModal(false);
                                            resetForm();
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Adding...' : 'Add Hardware'}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </Modal>
                )
            }

            {/* Details Modal */}
            {
                showDetailsModal && selectedHardware && editedHardware && (
                    <Modal
                        isOpen={showDetailsModal}
                        onClose={() => {
                            setShowDetailsModal(false);
                            setSelectedHardware(null);
                            setIsEditing(false);
                            setEditedHardware(null);
                        }}
                        title={`Hardware Details - ${selectedHardware.branchCode || selectedHardware.id}`}
                        size="lg"
                    >
                        <div className="p-6">
                            {!isEditing && (
                                <div className="mb-4 flex justify-end">
                                    <Button
                                        variant="secondary"
                                        onClick={handleEdit}
                                        className="flex items-center gap-2"
                                    >
                                        <Icon icon={FiEdit2} className="h-4 w-4" />
                                        Edit
                                    </Button>
                                </div>
                            )}

                            <div className="space-y-4">
                                {/* Row 1: Branch/Premises Code, Branch/Premises Name */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch/Premises Code</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.branchCode || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch/Premises Name</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.branchName || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Row 2: Cluster, Region */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Cluster</label>
                                        {isEditing ? (
                                            <TextField
                                                type="text"
                                                value={editedHardware.cluster || ''}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, cluster: e.target.value })}
                                                className="mt-1"
                                                placeholder="Enter cluster"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.cluster || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Region</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{(selectedHardware as any).regionName || selectedHardware.region || 'N/A'}</p>
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">(Auto-populated from branch location)</p>
                                    </div>
                                </div>

                                {/* Row 3: CCTV VENDOR */}
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">CCTV VENDOR</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{(selectedHardware as any).vendorName || selectedHardware.vendor || 'N/A'}</p>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">(Auto-populated from deployed CCTV companies)</p>
                                </div>

                                {/* Row 4: DVR/NVR, NO. OF DVR/NVR */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">DVR/NVR</label>
                                        {isEditing ? (
                                            <select
                                                value={editedHardware.dvrNvr || ''}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, dvrNvr: e.target.value })}
                                                className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="">Select Type</option>
                                                <option value="DVR">DVR</option>
                                                <option value="NVR">NVR</option>
                                            </select>
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.dvrNvr || 'N/A'}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">NO. OF DVR/NVR</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.noOfDvrNvr || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, noOfDvrNvr: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.noOfDvrNvr || 0}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Row 5: NO. OF CAMERA, HD/ANALOG */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">NO. OF CAMERA</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.noOfCamera || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, noOfCamera: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.noOfCamera || 0}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">HD/ANALOG</label>
                                        {isEditing ? (
                                            <select
                                                value={editedHardware.hdAnalog || ''}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, hdAnalog: e.target.value })}
                                                className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="">Select Type</option>
                                                <option value="HD">HD</option>
                                                <option value="ANALOG">ANALOG</option>
                                            </select>
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.hdAnalog || 'N/A'}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Row 6: NO. OF 5 MP CAMERA, NO. OF HDD */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">NO. OF 5 MP CAMERA</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.noOf5MpCamera || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, noOf5MpCamera: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.noOf5MpCamera || 0}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">NO. OF HDD</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.noOfHdd || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, noOfHdd: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.noOfHdd || 0}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button variant="secondary" onClick={() => {
                                    setShowDetailsModal(false);
                                    setSelectedHardware(null);
                                    setIsEditing(false);
                                    setEditedHardware(null);
                                }}>
                                    {isEditing ? 'Cancel' : 'Close'}
                                </Button>
                                {isEditing && (
                                    <Button
                                        variant="primary"
                                        onClick={async () => {
                                            if (!companyId || !editedHardware) return;

                                            try {
                                                // Prepare update data - exclude id and computed fields, include branchId
                                                const updateData: Partial<CCTVHardware> = {
                                                    ...editedHardware,
                                                    branchId: selectedHardware.branchId || editedHardware.branchId,
                                                };
                                                // Remove id and computed fields that shouldn't be saved
                                                // These fields are computed from branches and deployments
                                                delete (updateData as any).id;
                                                delete (updateData as any).regionName;
                                                delete (updateData as any).vendorName;
                                                delete (updateData as any).region; // Region comes from branch locationName
                                                delete (updateData as any).vendor; // Vendor comes from deployments

                                                // Check if this is a new entry (id starts with "new-")
                                                if (selectedHardware.id.startsWith('new-')) {
                                                    // Create new hardware entry
                                                    await createCCTVHardware(companyId, updateData);
                                                    setToastMessage('Hardware added successfully');
                                                } else {
                                                    // Update existing hardware entry
                                                    await updateCCTVHardware(companyId, selectedHardware.id, updateData);
                                                    setToastMessage('Hardware updated successfully');
                                                }

                                                setToastType('success');
                                                setShowToast(true);
                                                setIsEditing(false);

                                                // Refresh hardware list
                                                const hardwareList = await fetchCCTVHardware(companyId);
                                                setHardware(hardwareList);

                                                // Refresh the merged list (will be done by the filter effect)
                                                setSelectedHardware(null);
                                                setEditedHardware(null);
                                                setShowDetailsModal(false);
                                            } catch (err) {
                                                console.error(err);
                                                setToastMessage('Failed to save hardware');
                                                setToastType('error');
                                                setShowToast(true);
                                            }
                                        }}
                                    >
                                        Save Changes
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Modal>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                showDeleteModal && hardwareToDelete && (
                    <Modal
                        isOpen={showDeleteModal}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setHardwareToDelete(null);
                        }}
                        title="Confirm Deletion"
                        size="md"
                    >
                        <div className="p-6">
                            <div className="mb-4 text-center">
                                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                                    <Icon icon={FiAlertCircle} className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Are you sure?</h3>
                                <p className="mb-1 text-sm text-gray-600 dark:text-gray-400">Do you really want to delete this hardware record?</p>
                                <p className="mb-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    <span className="font-semibold">Branch:</span> {hardwareToDelete.branchCode || 'N/A'}
                                    <br />
                                    <span className="font-semibold">Item:</span> {hardwareToDelete.itemName || 'N/A'}
                                </p>
                                <p className="text-sm text-red-600 dark:text-red-400">This action cannot be undone.</p>
                            </div>
                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setHardwareToDelete(null);
                                    }}
                                    disabled={deleting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                                >
                                    {deleting ? 'Deleting...' : (
                                        <>
                                            <Icon icon={FiTrash2} className="h-4 w-4" />
                                            Delete
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                )
            }

            {/* Upload Progress Modal */}
            {
                uploading && (
                    <Modal
                        isOpen={uploading}
                        onClose={() => { }} // Prevent closing during upload
                        title="Importing Excel File"
                        size="md"
                    >
                        <div className="space-y-4 px-6">
                            <div className="text-center">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Processing your Excel file... Please wait.
                                </p>
                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                    <div
                                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                    {uploadProgress}% complete
                                </p>
                            </div>
                        </div>
                    </Modal>
                )
            }

            {/* Upload Results Modal */}
            <Modal
                isOpen={showUploadModal}
                onClose={handleModalClose}
                title="Bulk Upload Results"
                size="xl"
            >
                <div className="space-y-4 px-6">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-2 flex-wrap">
                            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                {uploadResults.filter(r => r.status === 'success').length} New
                            </span>
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                {uploadResults.filter(r => r.status === 'updated').length} Updated
                            </span>
                            <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                {uploadResults.filter(r => r.status === 'error').length} Failed
                            </span>
                            {uploadResults.filter(r => r.status === 'undone').length > 0 && (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                                    {uploadResults.filter(r => r.status === 'undone').length} Undone
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={downloadFinalReport}
                                className="flex items-center gap-2 text-xs"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Download Report
                            </Button>
                            {uploadResults.filter(r => r.status === 'error').length > 0 && (
                                <Button
                                    variant="secondary"
                                    onClick={downloadErrorReport}
                                    className="flex items-center gap-2 text-xs bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Download Errors
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Row
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Branch Code
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Message
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploadResults.map((result, index) => (
                                    <tr
                                        key={index}
                                        className={`border-b border-gray-200 dark:border-gray-700 ${result.status === 'error' ? 'bg-red-50 dark:bg-red-900/10' :
                                            result.status === 'updated' ? 'bg-blue-50 dark:bg-blue-900/10' :
                                                result.status === 'undone' ? 'bg-gray-50 dark:bg-gray-900/10' :
                                                    'bg-green-50 dark:bg-green-900/10'
                                            }`}
                                    >
                                        <td className="border-r border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                                            {result.rowNumber || index + 1}
                                        </td>
                                        <td className="border-r border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                                            {result.branchCode}
                                        </td>
                                        <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${result.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                                result.status === 'updated' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                                    result.status === 'undone' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300' :
                                                        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                }`}>
                                                {result.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {result.message}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {undoStack.length > 0 && uploadResults.some(r => r.status === 'updated') && (
                            <Button
                                variant="secondary"
                                onClick={handleUndo}
                                disabled={uploading}
                                className="bg-orange-50 hover:bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
                            >
                                Undo Updates
                            </Button>
                        )}
                        {undoStack.length > 0 && uploadResults.some(r => r.status === 'updated') && (
                            <Button
                                variant="primary"
                                onClick={handleKeep}
                            >
                                Keep Changes
                            </Button>
                        )}
                        {(!undoStack.length || !uploadResults.some(r => r.status === 'updated')) && (
                            <Button variant="secondary" onClick={handleModalClose}>
                                Close
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
}
